import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AsyncStateN } from '@blade/std-lib'
import { Button, Telegram2Icon } from '@blade/ui-kit'
import { authStateAtom, userAtom } from '@root/entities/auth'
import {
  reatomUnassignGoogle,
  redirectToGoogleAssignAction,
} from '@root/features/auth'
import {
  reatomTelegramAuthentication,
  reatomUnassignTelegram,
} from '@root/features/auth/telegram'
import { redirectToTwitterAssignAction } from '@root/features/auth/twitter/atoms'
import { GoogleIcon, XSocialIcon } from '@root/shared/ui-kit'

import { googleUnassignOpenAtom, telegramUnassignOpenAtom } from '../atoms'

import { SettingsItem } from './settings-item'
import { SettingsSocialsConfirm } from './settings-socials-confirm'
import { SettingsTwitterUnassignConfirm } from './socials/settings-twitter-unassign-confirm'

export const SettingsSocials: React.FC = () => {
  const { t } = useTranslation()
  const [_, setTelegramUnassignOpen] = useAtom(telegramUnassignOpenAtom)
  const [__, setGoogleUnassignOpen] = useAtom(googleUnassignOpenAtom)

  // eslint-disable-next-line @reatom/unit-naming-rule
  const unassignTelegramModel = useMemo(() => reatomUnassignTelegram(), [])
  const assignTelegramModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomTelegramAuthentication('assign'),
    [],
  )
  const unassignGoogleModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomUnassignGoogle(),
    [],
  )

  const [authState] = useAtom(authStateAtom)
  const [user] = useAtom(userAtom)
  const googleAuthRedirect = useAction(redirectToGoogleAssignAction)

  const [unassignTelegramState] = useAtom(unassignTelegramModel)

  const [assignTelegramState] = useAtom(assignTelegramModel)
  const assignTelegram = useAction(assignTelegramModel.execute)

  const [unassignGoogleState] = useAtom(unassignGoogleModel)

  const [twitterUnassignOpen, setTwitterUnassignOpen] = useState(false)
  const twitterAuthRedirect = useAction(redirectToTwitterAssignAction)

  const isSettingsDisabled = authState.type !== 'LoggedIn'

  useEffect(() => {
    if (AsyncStateN.isSuccess(unassignTelegramState)) {
      setTelegramUnassignOpen(false)
    }
  }, [unassignTelegramState, setTelegramUnassignOpen])
  useEffect(() => {
    if (AsyncStateN.isSuccess(unassignGoogleState)) {
      setGoogleUnassignOpen(false)
    }
  }, [unassignGoogleState, setGoogleUnassignOpen])

  return (
    <>
      <SettingsItem
        icon={<GoogleIcon size={18} />}
        title={t('settings.google.authorization')}
        disabled={isSettingsDisabled}
        endContent={
          user?.googleAuthEnabled ? (
            <Button
              variant="secondary"
              size="h28"
              radius="rounded"
              onClick={() => setGoogleUnassignOpen(true)}
            >
              {t('common.remove')}
            </Button>
          ) : (
            <Button
              onClick={googleAuthRedirect}
              variant="secondary"
              size="h28"
              radius="rounded"
              disabled={isSettingsDisabled}
            >
              {t('common.connect')}
            </Button>
          )
        }
      />
      <SettingsItem
        icon={<Telegram2Icon size={18} />}
        title={t('settings.telegram.authorization')}
        disabled={isSettingsDisabled}
        endContent={
          user?.tgId ? (
            <Button
              variant="secondary"
              size="h28"
              radius="rounded"
              onClick={() => setTelegramUnassignOpen(true)}
            >
              {t('common.remove')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="h28"
              radius="rounded"
              busy={assignTelegramState.loading}
              disabled={isSettingsDisabled}
              onClick={() => assignTelegram()}
            >
              {t('common.connect')}
            </Button>
          )
        }
      />

      <SettingsItem
        icon={<XSocialIcon size={18} />}
        title={t('settings.twitter.authorization')}
        endContent={
          user?.linkedAccounts?.twitter ? (
            <Button
              variant="secondary"
              size="h28"
              radius="rounded"
              onClick={() => setTwitterUnassignOpen(true)}
            >
              {t('common.remove')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="h28"
              radius="rounded"
              onClick={() => twitterAuthRedirect()}
            >
              {t('common.connect')}
            </Button>
          )
        }
      />

      <SettingsSocialsConfirm
        unassignTelegramModel={unassignTelegramModel}
        unassignGoogleModel={unassignGoogleModel}
      />

      <SettingsTwitterUnassignConfirm
        open={twitterUnassignOpen}
        onOpenChange={setTwitterUnassignOpen}
      />
    </>
  )
}
